import { Component, Input } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { OverlayBadgeModule } from 'primeng/overlaybadge';

// Services
import { ServiceBase } from 'src/app/@core/services/service-base';
import { Logger } from 'src/app/@core/services/logger/logger.service';

// Composants
import { BaseComponent, CONVERSATION_QUERY_PARAM } from 'src/app/@shared/components/abstract/base.component';
import { Conversation } from 'src/app/@shared/models/messaging/conversation.model';
import { ConversationEntityTypeEnum } from 'src/app/@shared/enums/conversation-entity-type.enum';
import { ConversationsDialogComponent } from '../conversations-dialog/conversations-dialog.component';

const LOGGER = new Logger('ConversationIndicator');

@Component({
	selector: 'app-conversation-indicator',
	imports: [
		ButtonModule,
		OverlayBadgeModule
	],
	templateUrl: './conversation-indicator.component.html',
	styleUrl: './conversation-indicator.component.scss'
})
export class ConversationIndicatorComponent extends BaseComponent {

	/*----------------------------------------------------------------------------------------
	 * Gestion de la vue
	 ----------------------------------------------------------------------------------------*/

	public isButtonVisible: boolean = false;
	public unreadCount: number = 0;

	// Contexte
	public service: ServiceBase | null = null;

	// Contexte explicite (mode @Input) : utilisé quand l'indicateur est posé hors d'une page
	// routée (ex. dialog d'édition d'une transaction). S'il est fourni, il court-circuite la
	// détection par URL.
	private _explicitContext: boolean = false;

	private _objectId: number | null = null;
	public get objectId(): number | null {
		return this._objectId;
	}
	@Input()
	public set objectId(value: number | null) {
		this._objectId = value;
		this._applyExplicitContext();
	}

	private _entityType: string | null = null;
	public get entityType(): string | null {
		return this._entityType;
	}
	@Input()
	public set entityType(value: string | null) {
		this._entityType = value;
		this._applyExplicitContext();
	}

	/** Le volet n'est ouvert d'office qu'une fois : le refermer ne doit pas le rouvrir. */
	private _initialConversationOpened: boolean = false;

	/*----------------------------------------------------------------------------------------
	 * Données
	 ----------------------------------------------------------------------------------------*/

	private _conversations: Conversation[] = [];
	public get conversations(): Conversation[] {
		return this._conversations;
	}
	public set conversations(value: Conversation[]) {
		let emptyArray: Conversation[] = [];
		this._conversations = emptyArray.concat(value);
		this.unreadCount = this._conversations.filter(c => !c.hasStatus(this.Status.Read)).length;
	}

	/*----------------------------------------------------------------------------------------
	 * Accesseurs
	 ----------------------------------------------------------------------------------------*/

	/**
	 * Fil désigné par l'adresse (`?conversation=`), lu directement sur l'URL courante (#995).
	 *
	 * L'indicateur lit l'adresse lui-même, comme il le fait déjà pour trouver son objet
	 * porteur (`_getContextFromUrl`) : le fil n'a donc pas à être relayé par la liste, la
	 * fenêtre et le formulaire. Corollaire utile — les écrans qui ont une page à eux
	 * (factures, devis) en héritent sans câblage, l'indicateur y étant posé par le shell.
	 *
	 * On lit l'URL du routeur plutôt que `queryParams`, alimenté au `ngOnInit` : en contexte
	 * explicite, les entrées s'appliquent avant, et l'ouverture ne doit dépendre d'aucun
	 * ordre d'initialisation.
	 */
	private get urlConversationId(): number | null {
		const raw = this.router.parseUrl(this.router.url).queryParams[CONVERSATION_QUERY_PARAM];
		const id = raw ? parseInt(raw, 10) : NaN;
		return Number.isInteger(id) && id > 0 ? id : null;
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes publiques
	 ----------------------------------------------------------------------------------------*/

	openConversationsView(initialConversationId: number | null = null): void {
		this.openDialog(ConversationsDialogComponent, 'CONVERSATIONS_DIALOG', {
			conversations: this.conversations,
			entityType: this.entityType,
			objectId: this.objectId,
			initialConversationId: initialConversationId,
		});
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes protégées
	 ----------------------------------------------------------------------------------------*/

	protected override loadData(): void {
		// En mode contexte explicite (@Input), le contexte n'est pas déduit de l'URL.
		if (this._explicitContext) {
			return;
		}

		LOGGER.debug('Chargement des conversations. URL:', this.router.url);
		LOGGER.debug('Paramètres de route :', this.routeParams);

		this._getContextFromUrl();
		this.isButtonVisible = !!(this.service && this.objectId && this.entityType);
		if (this.service && this.objectId) {
			this._loadConversations(this.service, this.objectId);
		} else {
			this._clearData();
		}
	}

	protected override subscribeToObservables(): void {
		this.subscriptions.add(
			this.conversationService.created$.subscribe(
				() => {
					LOGGER.debug('New conversation created, reloading conversations.');
					if (this.service && this.objectId) {
						this._loadConversations(this.service, this.objectId);
					}
				}
			)
		);

		this.subscriptions.add(
			this.conversationService.selected$.subscribe(
				(conversation: Conversation) => {
					// Mise à jour de la conversation dans la liste
					const index = this._conversations.findIndex(c => c.id === conversation.id);
					if (index !== -1) {
						this._conversations[index] = conversation;
					}
					this.conversations = this._conversations; // Trigger change detection
				}
			)
		);
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes privées
	 ----------------------------------------------------------------------------------------*/

	private _applyExplicitContext(): void {
		// Actif dès que entityType ET objectId sont fournis en @Input : on résout le service
		// correspondant et on charge les conversations, sans passer par la détection URL.
		if (!this._objectId || !this._entityType) {
			return;
		}
		this._explicitContext = true;
		this.service = this.getServiceForEntityType(this._entityType);
		this.isButtonVisible = true;
		this._loadConversations(this.service, this._objectId);
	}

	private _clearData(): void {
		this.conversations = [];
	}

	private _getContextFromUrl(): void {
		const url = this.router.url;

		switch (true) {
			case url.includes('/purchasing/invoice/'):
				LOGGER.debug('Contexte des conversations: Purchasing Invoice');
				const purchaseInvoiceId = this.routeParams?.get('invoiceId');
				if (purchaseInvoiceId) {
					this._objectId = parseInt(purchaseInvoiceId, 10);
					this.service = this.purchaseInvoiceService;
					this._entityType = ConversationEntityTypeEnum.PurchaseInvoice;
				}
				break;
			case url.includes('/sales/invoice/'):
				LOGGER.debug('Contexte des conversations: Sales Invoice');
				const salesInvoiceId = this.routeParams?.get('invoiceId');
				if (salesInvoiceId) {
					this._objectId = parseInt(salesInvoiceId, 10);
					this.service = this.salesInvoiceService;
					this._entityType = ConversationEntityTypeEnum.SalesInvoice;
				}
				break;
			case url.includes('/sales/quotation/'):
				LOGGER.debug('Contexte des conversations: Quotation');
				const quotationId = this.routeParams?.get('quotationId');
				if (quotationId) {
					this._objectId = parseInt(quotationId, 10);
					this.service = this.quotationService;
					this._entityType = ConversationEntityTypeEnum.Quotation;
				}
				break;
			default:
				LOGGER.debug('Aucun contexte spécifique détecté, effacement du contexte des conversations.');
				this.service = null;
				this._objectId = null;
				this._entityType = null;
				break;
		}
		if (this._objectId && isNaN(this._objectId)) this._objectId = null;
	}

	private _loadConversations(service: ServiceBase, objectId: number): void {
		LOGGER.info(`Chargement des conversations pour l'objectId: ${objectId} en utilisant le service:`, service.constructor.name);
		service.getConversations$(objectId).subscribe(
			conversations => {
				this.conversations = conversations;
				this._openInitialConversation();
			}
		);
	}

	/**
	 * Ouvre le volet sur le fil désigné par l'adresse, une fois les fils de l'objet chargés
	 * (#995). L'ouverture attend ce chargement : le volet a besoin de la liste des fils pour
	 * savoir si celui qui est demandé concerne bien cet objet.
	 *
	 * ⚠️ **Ce contrôle d'appartenance est le seul garde-fou.** L'adresse n'est plus filtrée en
	 * amont par la liste qui connaissait la ligne : sans lui, un identifiant quelconque
	 * ferait afficher la discussion d'un autre objet dans cette fenêtre. Un fil inconnu —
	 * supprimé, ou porté par un autre objet — n'ouvre donc rien.
	 */
	private _openInitialConversation(): void {
		if (this._initialConversationOpened) {
			return;
		}

		const conversationId = this.urlConversationId;
		if (!conversationId) {
			return;
		}
		if (!this.conversations.some(c => c.id === conversationId)) {
			LOGGER.debug('Fil demandé absent de cet objet, volet non ouvert', conversationId);
			return;
		}

		this._initialConversationOpened = true;
		this.openConversationsView(conversationId);
	}

}
